'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';

import {
  initConsent,
  recordConsent,
  reopenConsent,
  subscribeToConsent,
} from './consent-manager';
import { initGTMBridge } from './gtm-bridge';
import type { ConsentCategory, ConsentState } from './types';

// ----------------- CONTEXT SHAPE -----------------
type ConsentContextValue = {
  state: ConsentState;
  hasAnalytics: boolean;
  acceptAll: () => Promise<void>;
  declineAll: () => Promise<void>;
  saveCustom: (categories: ConsentCategory) => Promise<void>;
  reopen: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

// ----------------- CONTEXT PROVIDER -----------------
export function ConsentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConsentState>({ status: 'undecided' });

  const logConsent = useMutation(api.consent.logConsent);

  useEffect(() => {
    // 1. Resolve real consent state from cookie
    const resolved = initConsent();
    setState(resolved);

    // 2. Initialize GTM bridge AFTER consent state is known
    // Handles gtag('consent', 'update') when user makes a choice
    initGTMBridge();

    // 3. Subscribe to future changes
    const unsubscribe = subscribeToConsent((newState) => {
      setState(newState);
    });

    return unsubscribe;
  }, []);

  const logToServer = useCallback(
    async (
      record: ReturnType<typeof initConsent> extends { status: 'decided' }
        ? never
        : Parameters<typeof recordConsent>[2] extends (r: infer R) => any
          ? R
          : never
    ) => {
      await logConsent({
        sessionId: record.sessionId,
        timestamp: Date.now(),
        categories: record.categories,
        userAgent: navigator.userAgent,
        version: record.version,
        action: record.action,
      });
    },
    [logConsent]
  );

  const acceptAll = useCallback(async () => {
    await recordConsent(
      { necessary: true, analytics: true },
      'accept_all',
      logToServer
    );
  }, [logToServer]);

  const declineAll = useCallback(async () => {
    await recordConsent(
      { necessary: true, analytics: false },
      'decline_all',
      logToServer
    );
  }, [logToServer]);

  const saveCustom = useCallback(
    async (categories: ConsentCategory) => {
      await recordConsent(categories, 'custom', logToServer);
    },
    [logToServer]
  );

  const reopen = useCallback(() => {
    reopenConsent();
  }, []);

  // Derived from React state — never reads module-level variable directly
  // This prevents hydration mismatches on iOS Safari
  const hasAnalytics =
    state.status === 'decided' && state.record.categories.analytics;

  return (
    <ConsentContext.Provider
      value={{
        state,
        hasAnalytics,
        acceptAll,
        declineAll,
        saveCustom,
        reopen,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
}

// ----------------- HOOK -----------------
export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);

  if (!ctx) {
    throw new Error('useConsent must be used within a <ConsentProvider>');
  }

  return ctx;
}
